// The dot on the Appearance button (docs/OUTFITI_garderoba.md, 4.1): an outfit unlocked since
// the screen was last opened. Remembered in this browser only; nothing is stored on the server.

import { useSyncExternalStore } from 'react';
import { outfitKey, type OutfitRef } from '@lastday/shared';
import { useMe } from '@/api/hooks';

const KEY = 'ld.outfits.seen';
const subs = new Set<() => void>();
let cache: string | null | undefined;

function read(): string | null {
  if (cache !== undefined) return cache;
  try {
    cache = localStorage.getItem(KEY);
  } catch {
    cache = null;
  }
  return cache;
}

/** The outfits unlocked the last time the Appearance screen was open. */
export function seenOutfits(): Set<string> {
  try {
    return new Set(JSON.parse(read() ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

export function markOutfitsSeen(unlocked: OutfitRef[]) {
  const next = JSON.stringify(unlocked.map(outfitKey).sort());
  if (next === read()) return;
  cache = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* private mode: the dot comes back next time */
  }
  subs.forEach((l) => l());
}

/** True while some unlocked outfit has not been seen on the Appearance screen. */
export function useNewOutfit(): boolean {
  const raw = useSyncExternalStore(
    (l) => {
      subs.add(l);
      return () => subs.delete(l);
    },
    read,
  );
  const unlocked = useMe().data?.survivor?.unlockedOutfits;
  if (!unlocked?.length) return false;
  void raw;
  const seen = seenOutfits();
  return unlocked.some((o) => !seen.has(outfitKey(o)));
}
